import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import echo from '../lib/echo';

interface PositionUpdate {
    id: string;
    current_price: number;
    unrealized_pnl: number;
}

/**
 * Subscribes to trade/order/position events on the user's private channel.
 *
 * - trade.executed / order.updated  → full cache invalidation (list re-fetch)
 * - positions.updated               → surgical cache update of unrealized_pnl
 *   per position, no re-fetch needed
 *
 * @param customerId - the authenticated customer's id (from auth context)
 */
export function useTradesSocket(customerId: string | number | null | undefined) {
    const queryClient = useQueryClient();

    useEffect(() => {
        if (!customerId) return;

        const channel = echo.private(`user.${customerId}`);

        const invalidateTrades = () => {
            queryClient.invalidateQueries({ queryKey: ['trades'] });
            queryClient.invalidateQueries({ queryKey: ['positions'] });
        };

        const applyPositionUpdates = (payload: { positions: PositionUpdate[] }) => {
            if (!payload?.positions?.length) return;

            // Build a lookup map for O(1) updates
            const updates = new Map(payload.positions.map(p => [p.id, p]));

            // Patch every cached trades query that holds open positions
            queryClient.setQueriesData<any>({ queryKey: ['trades'] }, (old: any) => {
                if (!old?.trades) return old;
                return {
                    ...old,
                    trades: old.trades.map((trade: any) => {
                        const update = updates.get(trade.id);
                        if (!update) return trade;
                        return {
                            ...trade,
                            current_price: update.current_price,
                            unrealized_pnl: update.unrealized_pnl,
                        };
                    }),
                };
            });
        };

        channel.listen('.trade.executed', invalidateTrades);
        channel.listen('.order.updated', invalidateTrades);
        channel.listen('.positions.updated', applyPositionUpdates);

        return () => {
            echo.leaveChannel(`private-user.${customerId}`);
        };
    }, [customerId, queryClient]);
}
